// PDP media swap: clicking (or Enter/Space on) a secondary slot trades its
// media with the main slot, in place. Presentation (object-fit, radius) lives
// on the SLOTS, so swapped media adopts the slot's framing.
//
// No stale-reference handling needed: on variant change media-gallery.js
// replaces the whole <media-gallery> with fresh server HTML — swap state
// intentionally resets and this element re-upgrades automatically.
class GgMediaSwap extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onActivate);
    this.addEventListener('keydown', this.#onKeydown);
  }

  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest('[data-gg-slot="secondary"]')) return;
    event.preventDefault();
    this.#onActivate(event);
  };

  #onActivate = (event) => {
    const secondary = event.target.closest('[data-gg-slot="secondary"]');
    const main = this.querySelector('[data-gg-slot="main"]');
    if (!secondary || !main || !this.contains(secondary)) return;

    const fromSecondary = secondary.firstElementChild;
    const fromMain = main.firstElementChild;
    if (!fromSecondary || !fromMain) return;

    main.replaceChild(fromSecondary, fromMain);
    secondary.appendChild(fromMain);

    // Retarget responsive sizes per slot — srcset only upgrades, never blurs.
    this.#applySizes(main, this.dataset.mainSizes);
    this.#applySizes(secondary, this.dataset.secondarySizes);

    // Main slot adopts the real aspect ratio of whatever image now sits in it,
    // so the main image is never letterboxed/cropped after a swap.
    this.#applyMainRatio(main);
  };

  #applyMainRatio(main) {
    const img = main.querySelector('img');
    if (!img) return;
    const ratio = this.#ratioOf(img);
    if (ratio) main.style.setProperty('--gg-main-ratio', ratio);
  }

  // Prefer the intrinsic dimensions from width/height attrs (available before
  // load); fall back to naturalWidth/Height once decoded.
  #ratioOf(img) {
    const w = Number(img.getAttribute('width')) || img.naturalWidth;
    const h = Number(img.getAttribute('height')) || img.naturalHeight;
    return w && h ? w / h : null;
  }

  #applySizes(slot, sizes) {
    if (!sizes) return;
    slot.querySelectorAll('img').forEach((img) => {
      img.sizes = sizes;
    });
  }
}

if (!customElements.get('gg-media-swap')) {
  customElements.define('gg-media-swap', GgMediaSwap);
}
